import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import {
    Box,
    Button,
    GlobalStyles,
    Paper,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import { ReactNode, useRef, useState } from 'react';
import SupportEmailLink, { SUPPORT_EMAIL } from '~/components/SupportEmailLink';
import { ElementSize, useElementSize } from '~/hooks/useElementSize';
import { ConsoleLevel } from '~/utils/console-buffer';
import {
    ErrorDiagnostics,
    formatConsoleEntry,
    formatDiagnosticsReport,
    stackFrames,
    supportMailtoHref,
    withoutOrigin,
} from '~/utils/error-diagnostics';
import ErrorReportDialog from './ErrorReportDialog';

type ErrorPageProps = {
    status: number;
    title: string;
    /** Sentence-case explanation shown under the title. */
    description?: string;
    diagnostics?: ErrorDiagnostics;
    /**
     * Offers a "Try again" action that reloads the current page, for
     * failures a retry can plausibly fix. "Back to Start" leaves the
     * current URL, which loses a deep link.
     */
    retryable?: boolean;
};

type CopyState = 'idle' | 'copied' | 'failed';

const COPY_LABELS: Record<CopyState, string> = {
    idle: 'Copy Details',
    copied: 'Details Copied',
    failed: 'Copy Failed',
};

/**
 * Spacing, type sizes and how much console output the diagnostics layout
 * shows. The page never scrolls inside a panel, so a screenshot shows
 * everything the page shows; smaller screens get tighter spacing and fewer
 * console lines rather than a scrollbar. Copy Details carries everything.
 */
interface Density {
    /** MUI spacing units. */
    pagePadding: number;
    cardPadding: number;
    gap: number;
    panelPaddingX: number;
    panelPaddingY: number;
    rowGap: number;
    /** px */
    textSize: number;
    monospaceSize: number;
    lineHeight: number;
    consoleEntries: number;
    /** Lines shown per console entry; a single call can span many. */
    consoleEntryLines: number;
    componentStackLines: number;
}

const COMFORTABLE: Density = {
    pagePadding: 3,
    cardPadding: 3,
    gap: 2.5,
    panelPaddingX: 2,
    panelPaddingY: 1.5,
    rowGap: 1,
    textSize: 12,
    monospaceSize: 11,
    lineHeight: 1.6,
    consoleEntries: 25,
    consoleEntryLines: 6,
    componentStackLines: 12,
};

const DENSE: Density = {
    pagePadding: 1,
    cardPadding: 2,
    gap: 1.5,
    panelPaddingX: 1.5,
    panelPaddingY: 1,
    rowGap: 0.5,
    textSize: 11,
    monospaceSize: 10,
    lineHeight: 1.45,
    consoleEntries: 15,
    consoleEntryLines: 3,
    componentStackLines: 8,
};

/** Little height: fewer console lines, beside or below the stack trace. */
const SHORT: Density = {
    ...DENSE,
    cardPadding: 1.5,
    consoleEntries: 8,
    consoleEntryLines: 2,
};

/** Panels stack vertically, so the console gets the least room. */
const STACKED: Density = {
    ...DENSE,
    consoleEntries: 6,
    consoleEntryLines: 2,
};

/**
 * Space thresholds, CSS px: below the first the stack trace and console
 * stack vertically; below the next two the dense spacing is used; the two
 * identifier groups stay side by side down to the fourth; under the last
 * height the console shows fewer lines.
 */
const SIDE_BY_SIDE_MIN_WIDTH = 900;
const COMFORTABLE_MIN_WIDTH = 1440;
const COMFORTABLE_MIN_HEIGHT = 900;
const IDENTIFIER_COLUMNS_MIN_WIDTH = 760;
const SHORT_MAX_HEIGHT = 720;
const COMFORTABLE_MIN_VIEWPORT = `(min-width: ${COMFORTABLE_MIN_WIDTH}px) and (min-height: ${COMFORTABLE_MIN_HEIGHT}px)`;
const SIDE_BY_SIDE_MIN_VIEWPORT = `(min-width: ${SIDE_BY_SIDE_MIN_WIDTH}px)`;
const IDENTIFIER_COLUMNS_MIN_VIEWPORT = `(min-width: ${IDENTIFIER_COLUMNS_MIN_WIDTH}px)`;
const SHORT_VIEWPORT = `(max-height: ${SHORT_MAX_HEIGHT - 0.05}px)`;

interface Layout {
    density: Density;
    /** Stack trace above the console instead of beside it. */
    stacked: boolean;
    /** The two identifier groups side by side. */
    identifierColumns: boolean;
}

function densityFor(
    stacked: boolean,
    comfortable: boolean,
    short: boolean
): Density {
    if (stacked) {
        return STACKED;
    }
    if (short) {
        return SHORT;
    }
    return comfortable ? COMFORTABLE : DENSE;
}

/**
 * The layout for the space the page actually has. Inside the viewer layout
 * that is the content area beside the sidebar, not the window; the window
 * size is the fallback until the space is measured (or where it cannot be).
 */
function useLayout(space: ElementSize | undefined): Layout {
    const stackedViewport = !useMediaQuery(SIDE_BY_SIDE_MIN_VIEWPORT);
    const identifierColumnsViewport = useMediaQuery(
        IDENTIFIER_COLUMNS_MIN_VIEWPORT
    );
    const comfortableViewport = useMediaQuery(COMFORTABLE_MIN_VIEWPORT);
    const shortViewport = useMediaQuery(SHORT_VIEWPORT);
    const measured = space && space.widthPx > 0 && space.heightPx > 0;
    const stacked = measured
        ? space.widthPx < SIDE_BY_SIDE_MIN_WIDTH
        : stackedViewport;
    const comfortable = measured
        ? space.widthPx >= COMFORTABLE_MIN_WIDTH &&
          space.heightPx >= COMFORTABLE_MIN_HEIGHT
        : comfortableViewport;
    const short = measured ? space.heightPx < SHORT_MAX_HEIGHT : shortViewport;
    const identifierColumns = measured
        ? space.widthPx >= IDENTIFIER_COLUMNS_MIN_WIDTH
        : identifierColumnsViewport;
    return {
        density: densityFor(stacked, comfortable, short),
        stacked,
        identifierColumns,
    };
}

const CONSOLE_LEVEL_COLORS: Record<ConsoleLevel, string> = {
    error: 'error.main',
    warn: 'warning.main',
    info: 'text.secondary',
    debug: 'text.secondary',
    log: 'text.secondary',
};

const monospaceSx = (theme: Theme, density: Density) => ({
    fontFamily: theme.typography.fontFamilyMonospace,
    fontSize: density.monospaceSize,
    lineHeight: density.lineHeight,
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-all' as const,
});

const DiagnosticsRow = ({
    label,
    value,
    density,
}: {
    label: string;
    value: string | undefined;
    density: Density;
}) => (
    <>
        <Typography fontSize={density.textSize} color="text.secondary">
            {label}
        </Typography>
        <Typography
            fontSize={density.textSize}
            sx={(theme) => ({
                fontFamily: theme.typography.fontFamilyMonospace,
                color: value ? 'text.primary' : 'action.disabled',
                wordBreak: 'break-all',
                userSelect: 'all',
            })}
        >
            {value ?? 'n/a'}
        </Typography>
    </>
);

/** Label and value columns for a list of diagnostics rows. */
const DiagnosticsGroup = ({
    children,
    density,
}: {
    children: ReactNode;
    density: Density;
}) => (
    <Box
        display="grid"
        gridTemplateColumns="96px minmax(0, 1fr)"
        rowGap={density.rowGap}
        columnGap={2}
    >
        {children}
    </Box>
);

const SunkenPanel = ({
    heading,
    children,
    testId,
    density,
}: {
    heading: string;
    children: ReactNode;
    testId: string;
    density: Density;
}) => (
    <Box display="flex" flexDirection="column" gap={0.5} minWidth={0}>
        <Typography
            fontSize={density.textSize}
            fontWeight={500}
            color="text.secondary"
        >
            {heading}
        </Typography>
        <Box
            flex={1}
            border={1}
            borderColor="divider"
            borderRadius={1}
            bgcolor="background.sunken"
            px={density.panelPaddingX}
            py={density.panelPaddingY}
            data-testid={testId}
        >
            {children}
        </Box>
    </Box>
);

/** At most `maxLines` lines of an entry, noting how many were left out. */
function consoleEntryText(text: string, maxLines: number): string {
    const { shown, hiddenCount } = firstLines(text, maxLines);
    return hiddenCount > 0
        ? `${shown} … ${hiddenCount} more ${hiddenCount === 1 ? 'line' : 'lines'}`
        : shown;
}

/** The first `count` lines and how many were left out. */
function firstLines(
    text: string | undefined,
    count: number
): { shown: string; hiddenCount: number } {
    const lines = text ? text.split('\n') : [];
    return {
        shown: lines.slice(0, count).join('\n'),
        hiddenCount: Math.max(0, lines.length - count),
    };
}

type PanelProps = { diagnostics: ErrorDiagnostics; density: Density };

const StackPanel = ({ diagnostics, density }: PanelProps) => {
    const componentStack = firstLines(
        diagnostics.componentStack,
        density.componentStackLines
    );
    const origin = window.location.origin;
    return (
        <SunkenPanel
            heading="Stack trace"
            testId="error-stack"
            density={density}
        >
            <Box
                component="pre"
                sx={(theme) => ({
                    ...monospaceSx(theme, density),
                    m: 0,
                    color: 'text.secondary',
                })}
            >
                <Box component="span" color="text.primary" fontWeight={500}>
                    {diagnostics.errorName}: {diagnostics.errorMessage}
                </Box>
                {stackFrames(diagnostics.stack)
                    .map((frame) => `\n    ${withoutOrigin(frame, origin)}`)
                    .join('')}
                {componentStack.shown && (
                    <>
                        {'\n\n'}
                        <Box component="span" color="text.primary">
                            Component stack
                        </Box>
                        {`\n${componentStack.shown}`}
                        {componentStack.hiddenCount > 0 &&
                            `\n    … ${componentStack.hiddenCount} more in Copy Details`}
                    </>
                )}
            </Box>
        </SunkenPanel>
    );
};

const ConsolePanel = ({ diagnostics, density }: PanelProps) => {
    const { consoleEntries } = diagnostics;
    const shown = consoleEntries.slice(-density.consoleEntries);
    const origin = window.location.origin;
    const heading =
        consoleEntries.length > shown.length
            ? `Console (UTC), last ${shown.length} of ${consoleEntries.length}; all in Copy Details`
            : 'Console (UTC)';
    return (
        <SunkenPanel heading={heading} testId="error-console" density={density}>
            {shown.length === 0 ? (
                <Typography fontSize={density.textSize} color="text.secondary">
                    No console output was recorded.
                </Typography>
            ) : (
                <Box
                    component="pre"
                    sx={(theme) => ({ ...monospaceSx(theme, density), m: 0 })}
                >
                    {shown.map((entry, index) => (
                        <Box
                            // Entries have no ID; the list is a snapshot that
                            // never reorders.
                            key={`${entry.time}-${index}`}
                            component="span"
                            display="block"
                            color={CONSOLE_LEVEL_COLORS[entry.level]}
                        >
                            {consoleEntryText(
                                withoutOrigin(
                                    formatConsoleEntry(entry),
                                    origin
                                ),
                                density.consoleEntryLines
                            )}
                        </Box>
                    ))}
                </Box>
            )}
        </SunkenPanel>
    );
};

/**
 * Identifiers, stack trace and console output, all visible without
 * scrolling inside the page, so that a plain screenshot carries everything
 * support needs to find the Sentry event and the backend logs.
 */
const DiagnosticsContent = ({
    diagnostics,
    density,
    stacked,
    identifierColumns,
}: PanelProps & Omit<Layout, 'density'>) => (
    <Box
        px={density.cardPadding}
        pb={density.cardPadding}
        display="grid"
        gridTemplateColumns={
            stacked ? 'minmax(0, 1fr)' : 'minmax(0, 1fr) minmax(0, 1fr)'
        }
        gap={density.gap}
        alignItems="stretch"
    >
        <Box
            gridColumn="1 / -1"
            display="flex"
            flexDirection="column"
            gap={density.rowGap}
            border={1}
            borderColor="divider"
            borderRadius={1}
            bgcolor="background.sunken"
            px={density.panelPaddingX}
            py={density.panelPaddingY}
            lineHeight={1.4}
            data-testid="error-diagnostics-summary"
        >
            <Box
                display="grid"
                gridTemplateColumns={
                    identifierColumns
                        ? 'repeat(2, minmax(0, 1fr))'
                        : 'minmax(0, 1fr)'
                }
                rowGap={density.rowGap}
                columnGap={4}
            >
                {/* What finds the event in Sentry. */}
                <DiagnosticsGroup density={density}>
                    <DiagnosticsRow
                        density={density}
                        label="Error ID"
                        value={diagnostics.eventId}
                    />
                    <DiagnosticsRow
                        density={density}
                        label="Trace ID"
                        value={diagnostics.traceId}
                    />
                    <DiagnosticsRow
                        density={density}
                        label="Time (UTC)"
                        value={diagnostics.occurredAt}
                    />
                </DiagnosticsGroup>
                {/* What finds the backend logs and the application state. */}
                <DiagnosticsGroup density={density}>
                    <DiagnosticsRow
                        density={density}
                        label="Session ID"
                        value={diagnostics.backendSessionId}
                    />
                    <DiagnosticsRow
                        density={density}
                        label="Page"
                        value={diagnostics.page}
                    />
                    <DiagnosticsRow
                        density={density}
                        label="Version"
                        value={diagnostics.appVersion}
                    />
                </DiagnosticsGroup>
            </Box>
            <DiagnosticsGroup density={density}>
                <DiagnosticsRow
                    density={density}
                    label="Browser"
                    value={diagnostics.userAgent}
                />
            </DiagnosticsGroup>
        </Box>
        <StackPanel diagnostics={diagnostics} density={density} />
        <ConsolePanel diagnostics={diagnostics} density={density} />
    </Box>
);

const ErrorPage = ({
    status,
    title,
    description,
    diagnostics,
    retryable,
}: ErrorPageProps) => {
    const [reportOpen, setReportOpen] = useState(false);
    const [copyState, setCopyState] = useState<CopyState>('idle');
    const scrollAreaRef = useRef<HTMLDivElement>(null);
    const { density, stacked, identifierColumns } = useLayout(
        useElementSize(scrollAreaRef)
    );
    // The compact card for 401/404/503 always uses comfortable spacing.
    const spacing = diagnostics ? density : COMFORTABLE;

    const handleCopy = async () => {
        if (!diagnostics) {
            return;
        }
        try {
            await navigator.clipboard.writeText(
                formatDiagnosticsReport(diagnostics)
            );
            setCopyState('copied');
        } catch {
            // Clipboard can be unavailable (permissions, insecure context);
            // the details stay selectable on the page.
            setCopyState('failed');
        }
    };

    const actions = (
        <Box
            display="flex"
            alignItems="center"
            gap={1}
            flexWrap="wrap"
            flexShrink={0}
        >
            {diagnostics && (
                <>
                    <Button
                        variant="outlined"
                        startIcon={<ContentCopyIcon />}
                        onClick={handleCopy}
                    >
                        {COPY_LABELS[copyState]}
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<MailOutlineIcon />}
                        href={supportMailtoHref(SUPPORT_EMAIL, diagnostics)}
                    >
                        Email Support
                    </Button>
                    {diagnostics.eventId && (
                        <Button
                            variant="outlined"
                            startIcon={<FeedbackOutlinedIcon />}
                            onClick={() => setReportOpen(true)}
                        >
                            Send Report
                        </Button>
                    )}
                </>
            )}
            {retryable && (
                <Button
                    variant="contained"
                    onClick={() => window.location.reload()}
                >
                    Try again
                </Button>
            )}
            <Button variant={retryable ? 'outlined' : 'contained'} href="/">
                Back to Start
            </Button>
        </Box>
    );

    const heading = (
        <Box
            display="flex"
            flexDirection="column"
            gap={0.75}
            minWidth={0}
            flex="1 1 320px"
        >
            <Typography variant="h6" color="text.secondary">
                Error {status}
            </Typography>
            <Typography variant="h6" component="h1">
                {title}
            </Typography>
            {description && (
                <Typography
                    fontSize={spacing.textSize + 1}
                    color="text.secondary"
                >
                    {description}
                </Typography>
            )}
        </Box>
    );

    const supportNote = (
        <Typography
            fontSize={spacing.textSize}
            lineHeight={1.6}
            color="text.secondary"
            px={diagnostics ? spacing.cardPadding : 1}
            pb={diagnostics ? spacing.cardPadding : 0}
            textAlign={diagnostics ? 'left' : 'center'}
        >
            If the problem persists, contact <SupportEmailLink />
            {diagnostics && ' and include the copied details'}.
        </Typography>
    );

    return (
        // Its own scroll area: the viewer layout around it clips overflow,
        // so a page taller than the space scrolls here, from the top. The
        // area has no padding, so its measured size does not depend on the
        // density chosen from it.
        <Box
            ref={scrollAreaRef}
            height="100%"
            maxHeight="100vh"
            overflow="auto"
            boxSizing="border-box"
        >
            {/* Outside the viewer layout the page's parents have no height;
                give them the window's, so the area above can fill it. */}
            <GlobalStyles
                styles={{ 'html, body, #root': { height: '100%' } }}
            />
            <Box
                minHeight="100%"
                display="flex"
                justifyContent="center"
                px={diagnostics ? spacing.pagePadding : 2.5}
                py={diagnostics ? spacing.pagePadding : 6}
                boxSizing="border-box"
            >
                {diagnostics ? (
                    <Paper
                        variant="outlined"
                        sx={{
                            width: '100%',
                            maxWidth: 1600,
                            display: 'flex',
                            flexDirection: 'column',
                            bgcolor: 'background.raised',
                            boxShadow: 2,
                        }}
                    >
                        <Box
                            px={spacing.cardPadding}
                            pt={spacing.cardPadding}
                            pb={spacing.gap}
                            display="flex"
                            justifyContent="space-between"
                            alignItems="flex-start"
                            gap={2}
                            flexWrap="wrap"
                        >
                            {heading}
                            {actions}
                        </Box>
                        <DiagnosticsContent
                            diagnostics={diagnostics}
                            density={density}
                            stacked={stacked}
                            identifierColumns={identifierColumns}
                        />
                        <Box flex={1} />
                        {supportNote}
                    </Paper>
                ) : (
                    <Box
                        m="auto"
                        width="100%"
                        maxWidth={560}
                        display="flex"
                        flexDirection="column"
                        gap={2.5}
                    >
                        <Paper
                            variant="outlined"
                            sx={{
                                bgcolor: 'background.raised',
                                boxShadow: 2,
                                overflow: 'hidden',
                            }}
                        >
                            <Box px={3} pt={3} pb={2.5}>
                                {heading}
                            </Box>
                            <Box
                                px={3}
                                pb={3}
                                display="flex"
                                justifyContent="flex-end"
                            >
                                {actions}
                            </Box>
                        </Paper>
                        {supportNote}
                    </Box>
                )}
            </Box>

            {diagnostics?.eventId && (
                <ErrorReportDialog
                    open={reportOpen}
                    onClose={() => setReportOpen(false)}
                    eventId={diagnostics.eventId}
                />
            )}
        </Box>
    );
};

export default ErrorPage;
